import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkle, Trophy, ShieldWarning, Lightbulb, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const AGREEMENT_STYLE = {
  corretto:      { label: "Codice coerente",     Icon: CheckCircle,  tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  sovrastimato:  { label: "Codice sovrastimato", Icon: Warning,      tone: "text-amber-700 bg-amber-50 border-amber-200" },
  sottostimato:  { label: "Codice sottostimato", Icon: ShieldWarning,tone: "text-red-700 bg-red-50 border-red-200" },
};

function scoreClass(score) {
  if (score == null) return "text-slate-500";
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-600";
  return "text-red-600";
}

export default function DebriefPanel({ triageId, autoLoad = true }) {
  const [busy, setBusy] = useState(autoLoad);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const run = async () => {
    if (!triageId) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/ai/debrief/${triageId}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "AI non disponibile");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (autoLoad && triageId) run(); /* eslint-disable-next-line */ }, [triageId]);

  const agr = data ? (AGREEMENT_STYLE[data.code_agreement] || AGREEMENT_STYLE.corretto) : null;

  return (
    <div
      data-testid="ai-debrief"
      className="bg-gradient-to-br from-violet-50 to-white border-2 border-violet-200 rounded-md p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" className="text-violet-600" />
          <div>
            <div className="font-heading text-base font-semibold text-violet-900">Debriefing didattico</div>
            <div className="text-[10px] uppercase tracking-widest text-violet-700/70 font-semibold">Feedback educativo · non clinico</div>
          </div>
        </div>
        {data && (
          <div className={`text-right ${scoreClass(data.score)}`}>
            <div className="text-[10px] font-semibold uppercase tracking-widest">Punteggio</div>
            <div className="font-mono text-3xl font-bold" data-testid="debrief-score">{data.score}</div>
          </div>
        )}
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
          <CircleNotch size={18} className="animate-spin" /> Il tutor AI sta analizzando la simulazione…
        </div>
      )}

      {error && !busy && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 flex items-center justify-between gap-3">
          <span>{error}</span>
          <Button size="sm" variant="outline" onClick={run}>Riprova</Button>
        </div>
      )}

      {data && !busy && (
        <div className="space-y-4">
          <div className={`inline-flex items-center gap-2 text-xs font-medium border rounded-full px-3 py-1 ${agr.tone}`}>
            <agr.Icon size={14} weight="fill" /> {agr.label}
          </div>

          <div className="text-sm text-slate-800 leading-relaxed" data-testid="debrief-overall">
            {data.overall}
          </div>

          {data.strengths?.length > 0 && (
            <section>
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-800 flex items-center gap-1 mb-1">
                <Trophy size={14} weight="fill" /> Punti di forza
              </div>
              <ul className="space-y-1">
                {data.strengths.map((s, i) => (
                  <li key={`str-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.missed_red_flags?.length > 0 && (
            <section>
              <div className="text-xs font-semibold uppercase tracking-widest text-red-800 flex items-center gap-1 mb-1">
                <ShieldWarning size={14} weight="fill" /> Red flag potenzialmente non colte
              </div>
              <ul className="space-y-1">
                {data.missed_red_flags.map((r, i) => (
                  <li key={`rf-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-red-600 mt-0.5">•</span><span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.next_time_advice?.length > 0 && (
            <section>
              <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800 flex items-center gap-1 mb-1">
                <Lightbulb size={14} weight="fill" /> Prossima volta prova a…
              </div>
              <ul className="space-y-1">
                {data.next_time_advice.map((a, i) => (
                  <li key={`adv-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                    <Lightbulb size={12} weight="fill" className="text-indigo-500 mt-1 shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
